import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";

import { paper, fonts, blackA } from "./theme";
import { PageBack, MsSheet } from "./ms";
import { useGameState } from "./GameStateContext";
import { useWakeLock } from "./useWakeLock";

import EnterRoomCode from "./components/EnterRoomCode";
import WaitingRoom from "./components/WaitingRoom";
import ArticleSelection from "./components/ArticleSelection";
import WaitingForPlayers from "./components/WaitingForPlayers";
import PromptEntry from "./components/PromptEntry";
import DigitalPromptEntry from "./components/DigitalPromptEntry";
import WordPickerView from "./components/WordPickerView";
import Results from "./components/Results";
import Voting from "./components/Voting";
import VotingResults from "./components/VotingResults";
import RoundLeaderboard from "./components/RoundLeaderboard";
import GameOver from "./components/GameOver";
import HostControls from "./components/HostControls";
import PaperPlayerManage from "./components/PaperPlayerManage";
import PageTransition from "./components/PageTransition";

/**
 * The app shell is exactly the viewport, and it does not scroll.
 *
 * Scrolling belongs to MsBody, which is the only part of a screen that should
 * move — the timer, prompt and the footer CTA stay where the eye left them. That
 * only works if every ancestor is height-bounded: a `min-height` chain lets the
 * flex items grow to fit their content instead, which is what used to push the
 * whole page taller and take the header with it.
 */
const AppContainer = styled.div`
    flex: 1;
    width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    /* clip, not hidden — hidden would make the shell itself a scroll container
     * for iOS touch latching to strand pans in (see MsScreen). */
    overflow: clip;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
`;

const SpectatorBanner = styled.div`
    background: ${paper.ink};
    color: ${paper.paper};
    padding: calc(var(--safe-top) + 8px) 14px 8px;
    font-family: ${fonts.sans};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: center;
    box-shadow: 0 2px 8px ${blackA(0.25)};
    z-index: 50;
`;

/** Phases where the player is actively engaged and we want to keep the
 *  screen on. Lobby, home, and the game-over wrap-up are excluded — those
 *  are quiet moments where the user is happy for their phone to sleep. */
const ACTIVE_PHASES = new Set([
    "selecting_articles",
    "prompt_entry",
    "digital_prompt_entry",
    "word_picker",
    "voting",
    "results",
    "voting_results",
    "round_leaderboard",
]);

export default function App() {
    const { state, resetGame } = useGameState();
    const { phase, view, common, self } = state;
    const isSpectator = self?.role === "spectator";
    const isHost = !!self?.isHost;

    /** The in-game roster lives here rather than in HostControls so it can stand
     *  in for the phase screen instead of covering it — see the backdrop note
     *  below. The lobby has its own roster entry point (WaitingRoom's sheets). */
    const [rosterOpen, setRosterOpen] = useState(false);

    useWakeLock(ACTIVE_PHASES.has(phase));

    // Drop the sheet if we stop being host (transfer) or return to the lobby.
    useEffect(() => {
        if (!isHost || phase === "waiting_room") setRosterOpen(false);
    }, [isHost, phase]);

    const rosterSelf = rosterOpen && self?.isHost ? self : null;

    /** Server projection puts us in a generic waiting state for phases where
     *  the player has already submitted (word_picker after submit, voting
     *  after voting). For `selecting_articles` we let ArticleSelection handle
     *  its own waiting state since it morphs the picked card into a hero. */
    const showGenericWaiting =
        view && "isWaiting" in view && view.isWaiting && phase !== "selecting_articles";

    const renderPhase = () => {
        if (showGenericWaiting) {
            return (
                <PageTransition key="waiting" keyId="waiting">
                    <WaitingForPlayers view={view!} />
                </PageTransition>
            );
        }

        switch (phase) {
            case "enter_room_code":
                return <PageTransition key="enter_room_code" keyId="enter_room_code"><EnterRoomCode /></PageTransition>;
            case "waiting_room":
                return <PageTransition key="waiting_room" keyId="waiting_room"><WaitingRoom view={view} roomId={state.roomId} players={state.players} common={common} self={state.self} /></PageTransition>;
            case "selecting_articles":
                return <PageTransition key="selecting_articles" keyId="selecting_articles"><ArticleSelection view={view} /></PageTransition>;
            case "prompt_entry":
                return <PageTransition key="prompt_entry" keyId="prompt_entry"><PromptEntry view={view} self={state.self} /></PageTransition>;
            case "digital_prompt_entry":
                return <PageTransition key="digital_prompt_entry" keyId="digital_prompt_entry"><DigitalPromptEntry view={view} self={state.self} /></PageTransition>;
            case "word_picker":
                return <PageTransition key="word_picker" keyId="word_picker"><WordPickerView view={view} common={common} /></PageTransition>;
            case "results":
                return <PageTransition key="results" keyId="results"><Results view={view} /></PageTransition>;
            case "voting":
                return <PageTransition key="voting" keyId="voting"><Voting view={view} players={state.players} self={state.self} /></PageTransition>;
            case "voting_results":
                return <PageTransition key="voting_results" keyId="voting_results"><VotingResults view={view} players={state.players} self={state.self} /></PageTransition>;
            case "round_leaderboard":
                return <PageTransition key="round_leaderboard" keyId="round_leaderboard"><RoundLeaderboard view={view} players={state.players} self={state.self} /></PageTransition>;
            case "game_over":
                return <PageTransition key="game_over" keyId="game_over"><GameOver view={view} players={state.players} self={state.self} resetGame={resetGame} /></PageTransition>;
            default:
                return <PageTransition key="default" keyId="default"><EnterRoomCode /></PageTransition>;
        }
    };

    return (
        <AppContainer>
            {/* One persistent backdrop for the whole app. Mounted here (not per
                screen) so it survives phase transitions instead of tearing down
                and rebuilding 30 animated word layers on every navigation. It's
                fixed at z-index 0, behind the screen content, and is the app's
                only backdrop: sheets (settings / roster) stand in for a screen
                rather than covering it, so nothing ever repaints the paper. */}
            <PageBack />
            {!rosterSelf && <HostControls onOpenRoster={() => setRosterOpen(true)} />}
            {isSpectator && phase !== "enter_room_code" && phase !== "waiting_room" && (
                <SpectatorBanner>Spectating this round — you'll play next game</SpectatorBanner>
            )}
            {rosterSelf ? (
                <MsSheet>
                    <PaperPlayerManage
                        players={state.players}
                        selfId={rosterSelf.id}
                        isHost
                        onClose={() => setRosterOpen(false)}
                    />
                </MsSheet>
            ) : (
                /* No `mode="wait"` — outgoing and incoming PageTransition coexist briefly
                   so shared `layoutId` elements can FLIP between phases. PageTransition's
                   `is-exiting` class positions the old layer absolutely. */
                <AnimatePresence initial={false}>
                    {renderPhase()}
                </AnimatePresence>
            )}
        </AppContainer>
    );
}
